import { defineComponent as _defineComponent } from 'vue'

import type { MetaStoreType } from '@/stores/pinia/useMetaStore';

const __default__ = {
	name: 'App',
	metaInfo() {
		return (this as unknown as { metaStore: MetaStoreType }).metaStore.head;
	},
};

import { nextTick, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import { Platform } from '@/@types/graphql-types';
import AppHeader from '@/components/nav/AppHeader.vue';

import StyleInjector from '@/components/StyleInjector';

import {
	RegisterSessionStartDocument,
	RegisterSessionStartMutation,
	RegisterSessionStartMutationVariables,
} from '@/graphql/mutation/RegisterSessionStart.mutation';

import { useMutation } from '@/helpers/composables/useApollo';
import { enableScroll } from '@/helpers/scroll-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

import { useLoginFlow } from '@/features/auth/composables/useLoginFlow';
import { useExperimentInitialization } from '@/libraries/experiment';

import CanaryMarker from '@/ui-components/CanaryMarker.vue';

import '@/styles/global.scss';
import '@fortawesome/fontawesome-svg-core/styles.css';


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

const LowPriorityComponents = () =>
	import(/* webpackChunkName: "low-priority-components" */ '@/components/LowPriorityComponents.vue');

const StartPageJwtvContainer = () =>
	import(
		/* webpackChunkName: "start-page-jwtv-container" */ '@/components/experiments/startPageJwtv/StartPageJwtvContainer.vue'
	);

if (process.client) {
	import('@/helpers/tracking/cwv-helper').then(({ initCoreWebVitalsMonitor }) => initCoreWebVitalsMonitor({}));
}

const metaStore = useMetaStore();
const routingStore = useRoutingStore();
const languageStore = useLanguageStore();
const userStore = useUserStore();

const route = useRoute();
const router = useRouter();

const { initializeExperiments } = useExperimentInitialization();
const { initLoginFlow } = useLoginFlow();

const {
	mutate: registerSessionStart,
	onError,
	onDone,
} = useMutation<RegisterSessionStartMutation, RegisterSessionStartMutationVariables>(RegisterSessionStartDocument);

onError(error => {
	captureMessageForSentry('SSR App: Unable to fire Register Session Start Mutation', {
		where: '[SSRApp.vue] onMounted()',
		error,
	});

	// if session start fails we still need to mark experiments as loaded so anything
	// gated on `experimentsLoaded` (e.g. ads) isn't blocked forever
	initializeExperiments([]);
});

onDone(({ data }) => {
	const experiments = data?.registerSessionStart.experiments ?? [];
	initializeExperiments(
		experiments.map(el => ({
			countries: el.countries,
			languages: el.languages,
			name: el.name,
			sample: el.sample,
			variant: el.variant ?? null,
		}))
	);
});

watch(
	() => languageStore.webLocale,
	async (webLocale, oldWebLocale) => {
		if (webLocale === oldWebLocale) return;
		await router.jw_changeLocale(webLocale);
		await languageStore.initLanguage();
	},
	{ immediate: false }
);

onMounted(() => {
	void initLoginFlow();

	nextTick(() => {
		const int = setInterval(() => {
			// remove loader
			const loader = document.querySelector('#loader');
			loader?.parentNode?.removeChild(loader);
			clearInterval(int);
		}, 50);

		// reactivate scroll on page rendered
		enableScroll();
	});

	// this causes interaction lag on safari browsers
	// userStore.setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone),
	registerSessionStart({
		input: {
			platform: Platform.Web,
			language: languageStore.language,
			country: languageStore.country,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		},
	});

	TrackingHelper.isInitializedPromise.then(() => {
		//need to initialize PASubgenres for it to populate trackers as SEO user
		TrackingHelper.refreshPermanentAudiencesSubgenres([]);
		TrackingHelper.refreshPermanentAudiences(userStore.permanentAudiences);

		// route.fullPath is for Home as first page, which has incomplete urlMetadata
		if ((routingStore.urlMetadata?.fullPath ?? route.fullPath) == null) return;

		TrackingHelper.trackAudienceRefresh(userStore.permanentAudiences);
	});
});

expose({ metaStore });

return { __sfc: true,LowPriorityComponents, StartPageJwtvContainer, metaStore, routingStore, languageStore, userStore, route, router, initializeExperiments, initLoginFlow, registerSessionStart, onError, onDone, AppHeader, StyleInjector, CanaryMarker }
}

})